import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import {
  MonitoringActiveItemCard,
  MonitoringActiveItemGrid,
  formatMonitoringActiveSince,
} from './MonitoringActiveItemCard';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { apiService } from '../../services/api';
import { LoadingSpinner } from '../LoadingSpinner';
import { AssuntoMonitoringActivationLogList } from '../AssuntoMonitoringActivationLogList';
import { ChangeEmailDeliveryLogList } from '../ChangeEmailDeliveryLogList';

type Props = {
  enabled?: boolean;
};

export function AssuntosMonitoringPanel({ enabled = true }: Props) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);

  const { data, isLoading, refetch } = useQuery(
    ['assuntos-monitoring-history'],
    () => apiService.getAssuntosMonitoringHistory(),
    { enabled, refetchOnWindowFocus: false },
  );

  const { data: emailLogs, isLoading: emailLoading } = useQuery(
    ['assuntos-monitoring-delivery-logs'],
    () => apiService.getAssuntosMonitoringDeliveryLogs(),
    { enabled, refetchOnWindowFocus: false },
  );

  const toggleMutation = useMutation(
    async ({ assuntoKey, activate }: { assuntoKey: string; activate: boolean }) => {
      if (activate) {
        return apiService.activateAssuntoMonitoringByKey(assuntoKey);
      }
      return apiService.deactivateAssuntoMonitoring(assuntoKey);
    },
    {
      onSuccess: (result) => {
        toast.success(result.message);
        void refetch();
        queryClient.invalidateQueries(['assuntos-monitoring']);
        queryClient.invalidateQueries(['assuntos-monitoring-history']);
        queryClient.invalidateQueries(['dashboard-changes']);
      },
      onError: () => toast.error('Erro ao alterar monitoramento do assunto'),
    },
  );

  const assuntos = data?.assuntos ?? [];
  const history = data?.history ?? [];
  const emails = emailLogs ?? [];
  const activeCount = assuntos.filter((a) => a.is_monitored).length;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <LoadingSpinner message="Carregando assuntos..." />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {activeCount > 0
          ? `${activeCount} assunto(s) ativo(s) gerando notificações (e-mail D+1 e sininho).`
          : 'Nenhum assunto ativo. Ative no Dashboard → filtro de assuntos ou abaixo.'}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          aria-label="Abas do histórico de assuntos"
        >
          <Tab label={`Assuntos monitorados (${activeCount})`} />
          <Tab label={`Logs de ativação (${history.length})`} />
          <Tab label={`E-mails (${emails.length})`} />
        </Tabs>
      </Box>

      {activeTab === 0 &&
        (assuntos.length > 0 ? (
          <MonitoringActiveItemGrid>
            {assuntos.map((item) => (
              <MonitoringActiveItemCard
                key={item.assunto_key}
                title={item.assunto_label}
                subtitle={
                  item.is_monitored
                    ? formatMonitoringActiveSince(item.last_event?.timestamp)
                    : `Último: ${formatMonitoringActiveSince(item.last_event?.timestamp)}`
                }
                isActive={item.is_monitored}
                onToggle={(checked) =>
                  toggleMutation.mutate({ assuntoKey: item.assunto_key, activate: checked })
                }
                toggleLoading={toggleMutation.isLoading}
              />
            ))}
          </MonitoringActiveItemGrid>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">Nenhum assunto monitorado.</Typography>
          </Box>
        ))}

      {activeTab === 1 && <AssuntoMonitoringActivationLogList history={history} />}

      {activeTab === 2 && <ChangeEmailDeliveryLogList logs={emails} loading={emailLoading} />}
    </>
  );
}
